// frontend/src/pages/admin/DatabaseLeads.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Import only the database API function, remove CYC sync
import { getDatabaseLeads } from '../../api/database';

// Import UI components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';

// Import icons
import { Search, Plus, RefreshCw } from 'lucide-react';

const DatabaseLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [source, setSource] = useState('all'); // 'all', 'manual', 'import'
  const [retryCount, setRetryCount] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  const [retryTimer, setRetryTimer] = useState(null);
  const MAX_RETRIES = 3;

  // Fetch leads from database with retry mechanism
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching database leads with source:', source);
      const response = await getDatabaseLeads(source);
      console.log('Full database leads response:', response);

      if (response.success) {
        // Make sure we handle both possible response formats
        const leadsData = Array.isArray(response.data) ? response.data : 
                         (response.data.data && Array.isArray(response.data.data) ? response.data.data : []);
        
        console.log('Setting leads state with:', leadsData);
        setLeads(leadsData);
        setRetryCount(0); // Reset retry count on success
        setRetryAfter(0); // Reset retry after on success
      } else {
        setError(response.message || 'Failed to fetch leads');
      }
    } catch (err) {
      console.error('Error in DatabaseLeads:', err);
      
      // Handle rate limiting
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers['retry-after'] || 5;
        setError(`Rate limit exceeded. Retrying in ${retryAfter} seconds...`);
        
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            fetchLeads();
          }, retryAfter * 1000);
          return;
        }
      }
      
      // Handle other errors
      setError(err.message || 'Failed to fetch leads. Please try again later.');
    } finally {
      setLoading(false); // Make sure to set loading to false in all cases
    }
  }, [source, retryCount]);

  // Add retryTimer to the dependency array
  useEffect(() => {
    if (retryAfter > 0) {
      // Clear any existing timer
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      
      // Set up a new timer
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setRetryAfter(0);
        fetchLeads();
      }, retryAfter * 1000);
      
      setRetryTimer(timer);
    } else {
      fetchLeads();
    }
    
    // Clean up timer on unmount
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [fetchLeads, retryAfter, retryTimer]);

  // Filter leads based on search term
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (lead.id && lead.id.toString().includes(searchLower)) ||
      (lead.name && lead.name.toLowerCase().includes(searchLower)) ||
      (lead.email && lead.email.toLowerCase().includes(searchLower)) ||
      (lead.state && lead.state.toLowerCase().includes(searchLower))
    );
  });

  // Handle manual retry
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Database Leads</h1>
        <div className="flex items-center space-x-4">
          <select 
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="px-3 py-2 border rounded-md bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Leads</option>
            <option value="manual">Manually Added</option>
            <option value="import">Imported</option>
          </select>
          <Button
            onClick={() => navigate('/admin/leads/new')}
            variant="outline"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Lead
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="flex justify-between items-center">
            <span>{error}</span>
            {retryAfter > 0 ? (
              <span className="text-sm">Retrying in {retryAfter} seconds...</span>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRetry}
                className="ml-2 inline-flex items-center"
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Retry
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Your Database Leads</CardTitle>
          <CardDescription>
            These leads are stored in your database and can be used for email campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                className="pl-10 pr-4 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="text-sm text-gray-500">
              {filteredLeads.length} leads found
            </div>
          </div>

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex justify-center">
                        <div className="h-6 w-6 animate-spin text-primary">Loading...</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="text-gray-500">
                        {searchTerm ? 'No leads match your search' : 'No leads in database'}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>{lead.id}</TableCell>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.state || 'N/A'}</TableCell>
                      <TableCell>{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge variant={lead.status === 'purchased' ? 'success' : 'default'}>
                          {lead.status || 'new'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/emails/${lead.id}`)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Manage Emails
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/leads/${lead.id}`)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Edit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseLeads; 